import React, { useState, useContext, useEffect, useRef } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { motion, useAnimation } from 'framer-motion';
import ResumeButton from '../components/styled/ResumeButton';
import { collection, doc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { addMessageToFirestore } from '../services/contactService';
import SectionHeader from '../components/styled/SectionHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaperPlane,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';

// Form card spring entrance
const formCardVariants = {
    hidden: { opacity: 0, y: 24, scale: 0.98 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: 'spring', stiffness: 180, damping: 22 },
    },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBMISSION_TIMEOUT_MS = 15000;

const Contact = () => {
    const { theme, themes } = useContext(ThemeContext);
    const isLight = themes[theme]?.isIconDark;
    const shake = useAnimation();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);
    const submittingRef = useRef(false);
    const messageDocumentRef = useRef(null);

    // Auto-clear success message after 5 seconds
    useEffect(() => {
        if (status.type === 'success') {
            const timer = setTimeout(() => setStatus({ type: '', text: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const triggerError = (text) => {
        setStatus({ type: 'error', text });
        shake.start({ x: [0, -8, 8, -6, 6, -3, 3, 0], transition: { duration: 0.4 } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return;

        if (!name.trim() || !email.trim() || !message.trim()) {
            triggerError('Please fill in all fields.');
            return;
        }

        if (!EMAIL_REGEX.test(email.trim())) {
            triggerError('Please enter a valid email address.');
            return;
        }

        submittingRef.current = true;
        setLoading(true);
        setStatus({ type: '', text: '' });
        let timeoutId;

        try {
            if (!navigator.onLine) {
                throw new Error('offline');
            }

            // Reuse this document on retry so a delayed write cannot create duplicates.
            if (!messageDocumentRef.current) {
                messageDocumentRef.current = doc(collection(db, 'messages'));
            }

            const writePromise = addMessageToFirestore({
                name: name.trim(),
                email: email.trim(),
                message: message.trim(),
            }, messageDocumentRef.current);

            await Promise.race([
                writePromise,
                new Promise((_, reject) => {
                    timeoutId = setTimeout(() => reject(new Error('timeout')), SUBMISSION_TIMEOUT_MS);
                }),
            ]);

            messageDocumentRef.current = null;
            setName('');
            setEmail('');
            setMessage('');
            setStatus({ type: 'success', text: 'Message sent successfully!' });
        } catch (error) {
            console.error('Error adding message:', error);
            if (error.message === 'offline') {
                triggerError('You appear to be offline. Reconnect and try again.');
            } else if (error.message === 'timeout') {
                triggerError('Sending is taking longer than expected. Check your connection, then retry safely.');
            } else {
                triggerError('Failed to send message. Please try again.');
            }
        } finally {
            clearTimeout(timeoutId);
            submittingRef.current = false;
            setLoading(false);
        }
    };

    // Theme-adaptive input styles: quiet at rest, crisp on focus
    const inputClasses = `w-full px-3.5 py-2 text-sm rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-1 disabled:opacity-60 ${
        isLight
            ? 'bg-black/[0.03] border-black/10 text-slate-900 placeholder:text-black/35 hover:border-black/20 focus:border-black/35 focus:ring-black/15'
            : 'bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 hover:border-white/20 focus:border-white/35 focus:ring-white/20'
    }`;

    const labelClasses = `block text-xs font-semibold uppercase tracking-wider mb-1.5 opacity-60 ${themes[theme].text}`;

    return (
        <div className="w-full max-w-4xl mx-auto px-4 pt-2 pb-5 flex flex-col items-center justify-start">
            {/* Standardized Portfolio Header */}
            <SectionHeader
                badgeLabel="Direct Line"
                badgeIcon={faPaperPlane}
                title="Get in Touch"
                subtitle="Inquiries regarding iOS architecture, consulting, or engineering collaboration"
            />

            {/* Entrance */}
            <motion.div
                variants={formCardVariants}
                initial="hidden"
                animate="visible"
                className="w-full max-w-sm"
            >
                {/* Shake on error */}
                <motion.div
                    animate={shake}
                    className={`relative w-full p-5 rounded-2xl border shadow-lg backdrop-blur-xl transition-colors duration-300 ${
                        isLight
                            ? 'bg-black/[0.02] border-black/10 shadow-black/5'
                            : 'bg-white/[0.03] border-white/10 shadow-black/20'
                    }`}
                >
                    {/* Fine top-edge highlight */}
                    <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent ${
                            isLight ? 'via-black/25' : 'via-white/35'
                        }`}
                    />

                    <form className="space-y-3.5" onSubmit={handleSubmit} noValidate>
                        {/* Name */}
                        <div>
                            <label htmlFor="contact-name" className={labelClasses}>
                                Name
                            </label>
                            <input
                                id="contact-name"
                                type="text"
                                autoComplete="name"
                                placeholder="Your name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={loading}
                                className={inputClasses}
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="contact-email" className={labelClasses}>
                                Email
                            </label>
                            <input
                                id="contact-email"
                                type="email"
                                autoComplete="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                className={inputClasses}
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label htmlFor="contact-message" className={labelClasses}>
                                Message
                            </label>
                            <textarea
                                id="contact-message"
                                placeholder="How can I help?"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={3}
                                disabled={loading}
                                className={`${inputClasses} resize-none leading-relaxed`}
                            />
                        </div>

                        {/* Submit */}
                        <div className="pt-0.5 flex min-h-[40px] items-center justify-center">
                            {loading ? (
                                <div className="flex items-center justify-center gap-2 text-sm font-medium">
                                    <FontAwesomeIcon icon={faSpinner} spin className="text-emerald-500" />
                                    <span className={`${themes[theme].text} opacity-80`}>Sending...</span>
                                </div>
                            ) : (
                                <ResumeButton
                                    title="Send Message"
                                    className={`w-full sm:w-auto px-8 py-2.5 ${themes[theme].text}`}
                                />
                            )}
                        </div>

                        {/* Status feedback */}
                        <div aria-live="polite">
                            {status.text && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`p-3 rounded-xl border flex items-center justify-center gap-2 text-xs font-medium ${
                                        status.type === 'success'
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                                            : 'bg-rose-500/10 text-rose-400 border-rose-500/25'
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={status.type === 'success' ? faCheckCircle : faExclamationCircle}
                                        className="text-sm shrink-0"
                                    />
                                    <span>{status.text}</span>
                                </motion.div>
                            )}
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default Contact;
